import { ReactNode, useMemo } from 'react';
import { Dictionary } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import {
  MarketsState,
  useOptionMarketsState,
} from '#/features/options/markets-context';

export interface MarketsRoutingState {
  readonly status: 'loading' | 'error' | 'done';
  readonly error: string;
  readonly assets: string[];
  readonly expiryDates: Dictionary<string[]>;
  readonly defaultAsset: string | null;
  readonly defaultExpiryDate: string | null;
}

export const [useMarketsRouting, MarketsRoutingStateCtx] =
  createSafeContext<MarketsRoutingState>('MarketsRoutingState');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OptionMarketsRoutingStateProvider(props: ProviderProps) {
  const marketsState = useOptionMarketsState();

  const routingState = useMemo(() => deriveState(marketsState), [marketsState]);

  return (
    <MarketsRoutingStateCtx value={routingState}>
      {props.children}
    </MarketsRoutingStateCtx>
  );
}

function deriveState(state: MarketsState): MarketsRoutingState {
  const assets = deriveAssets(state);
  const expiryDates = deriveExpiryDates(state);
  const defaultAsset = deriveDefaultAsset(assets);
  const defaultExpiryDate = deriveDefaultExpiryDate(defaultAsset, expiryDates);

  return {
    status: state.status,
    error: state.error,
    assets,
    expiryDates,
    defaultAsset,
    defaultExpiryDate,
  };
}

function deriveAssets(state: MarketsState): MarketsRoutingState['assets'] {
  const assets = new Set<string>();

  Object.values(state.markets.entities).forEach((market) =>
    assets.add(`${market!.base_currency}-${market!.quote_currency}`),
  );

  return Array.from(assets);
}

function deriveExpiryDates(
  state: MarketsState,
): MarketsRoutingState['expiryDates'] {
  const expiryDates = new Map<string, Set<string>>();

  Object.values(state.markets.entities).forEach((market) => {
    const { symbol } = market!;
    const [baseCurrency, quoteCurrency, expiryDate] = symbol.split('-');
    const asset = `${baseCurrency}-${quoteCurrency}`;

    if (!expiryDates.has(asset)) expiryDates.set(asset, new Set<string>());

    expiryDates.get(asset)!.add(expiryDate!);
  });

  return JSON.parse(
    JSON.stringify(expiryDates, transformToSerializable),
  ) as MarketsRoutingState['expiryDates'];
}

function deriveDefaultAsset(
  assets: MarketsRoutingState['assets'],
): MarketsRoutingState['defaultAsset'] {
  return assets[0] ?? null;
}

function deriveDefaultExpiryDate(
  defaultAsset: MarketsRoutingState['defaultAsset'],
  expiryDates: MarketsRoutingState['expiryDates'],
): MarketsRoutingState['defaultAsset'] {
  if (defaultAsset == null) return null;

  return expiryDates[defaultAsset]?.[0] ?? null;
}

/** Utility function to replace `Map` and `Set` as `Object` and `Array` */
function transformToSerializable(_key: string, value: unknown) {
  if (value instanceof Map) {
    return Object.fromEntries(value); // eslint-disable-line @typescript-eslint/no-unsafe-return
  }

  if (value instanceof Set) {
    return Array.from(value); // eslint-disable-line @typescript-eslint/no-unsafe-return
  }

  return value;
}
